import {FC, useEffect, useState} from "react";
import classNames from "classnames/dedupe";
import {cn} from "src/helpers/bem";
import "./styles.scss";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
} from "@mui/material";
import {
  useCarouselSettings,
  useUpdateCarouselSettings,
} from "src/hooks/banners";

const b = cn("banner-carousel-dialog");

interface Props {
  className?: string;
  open: boolean;
  onClose: () => void;
  onSaved?: (message: string) => void;
}

interface FormState {
  aspectRatio: string;
  autoScrollSeconds: string;
  loop: boolean;
}

const emptyForm: FormState = {
  aspectRatio: "",
  autoScrollSeconds: "",
  loop: false,
};

export const BannerCarouselSettingsDialog: FC<Props> = (props) => {
  const {className, open, onClose, onSaved} = props;

  const [form, setForm] = useState<FormState>(emptyForm);

  const {data, isFetching, isError} = useCarouselSettings(open);
  const updateMutation = useUpdateCarouselSettings();

  useEffect(() => {
    if (open && data) {
      setForm({
        aspectRatio: data.aspectRatio ?? "",
        autoScrollSeconds:
          data.autoScrollSeconds != null ? String(data.autoScrollSeconds) : "",
        loop: Boolean(data.loop),
      });
    }
    if (open) {
      updateMutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, data]);

  const patch = (partial: Partial<FormState>) =>
    setForm((prev) => ({...prev, ...partial}));

  const submitErrorText = updateMutation.isError
    ? ((updateMutation.error as any)?.response?.data?.error ??
      "Не удалось сохранить настройки")
    : null;

  const handleSubmit = () => {
    updateMutation.mutate(
      {
        aspectRatio: form.aspectRatio || undefined,
        autoScrollSeconds: form.autoScrollSeconds
          ? Number(form.autoScrollSeconds)
          : undefined,
        loop: form.loop,
      },
      {
        onSuccess: () => {
          onSaved?.("Настройки карусели сохранены");
          onClose();
        },
      },
    );
  };

  return (
    <Dialog
      open={open}
      onClose={updateMutation.isPending ? undefined : onClose}
      className={classNames(b(), className)}
    >
      <DialogTitle>Настройки карусели</DialogTitle>

      <DialogContent className={b("content")}>
        {isFetching ? (
          <div className={b("loading")}>
            <CircularProgress size={32} />
          </div>
        ) : (
          <>
            {isError && (
              <Alert severity="error" className={b("error")}>
                Не удалось загрузить настройки
              </Alert>
            )}

            <TextField
              label="Соотношение сторон"
              size="small"
              placeholder="например, 16:9"
              value={form.aspectRatio}
              onChange={(e) => patch({aspectRatio: e.target.value})}
            />

            <TextField
              label="Автопрокрутка (сек)"
              type="number"
              size="small"
              inputProps={{min: 0}}
              value={form.autoScrollSeconds}
              onChange={(e) => patch({autoScrollSeconds: e.target.value})}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={form.loop}
                  onChange={(_, checked) => patch({loop: checked})}
                />
              }
              label="Зациклить прокрутку"
            />

            {submitErrorText && (
              <Alert severity="error" className={b("error")}>
                {submitErrorText}
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={updateMutation.isPending}>
          Отмена
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={updateMutation.isPending || isFetching}
        >
          Сохранить
        </Button>
      </DialogActions>
    </Dialog>
  );
};
